import { ArrowCircleBrokenDown, BankNote03, CheckVerified02, Hourglass03 } from "@untitledui/icons";
import { Countdown, HelpTip, IconChip, JalaliDate, Money } from "@mfa-aff/ui";
import { Button } from "@mfa-aff/ui/components/base/buttons/button";
import { BackgroundPattern } from "@mfa-aff/ui/components/shared-assets/background-patterns/index";
import type { LifetimeCommissionView, PartnerBalancesView } from "@mfa-aff/shared";
import { withdrawGate } from "../wallet/withdrawGate";

const cardClass = "relative flex flex-col gap-5 rounded-2xl bg-primary p-5 shadow-xs ring-1 ring-secondary ring-inset md:p-6";

/**
 * The money strip (spec §11): withdrawable + withdraw button, pending with countdown, and the big
 * «پرداخت‌شده تا امروز» box. Card anatomy follows Untitled UI's Banking/Fintech balance cards.
 */
export const MoneyStrip = ({ balances, lifetime, payoutsEnabled }: { balances: PartnerBalancesView; lifetime?: LifetimeCommissionView; payoutsEnabled: boolean }) => {
    const gate = withdrawGate(payoutsEnabled, balances.withdrawableRial);
    return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6 xl:grid-cols-[1fr_1fr_1.3fr]">
        {/* Available */}
        <section className={cardClass} aria-labelledby="available-label">
            <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <p id="available-label" className="flex items-center gap-1.5 text-sm font-medium text-tertiary">
                        قابل برداشت
                        <HelpTip term="available" />
                    </p>
                    <Money rial={balances.withdrawableRial} size="display-sm" className="font-semibold text-success-primary dark:text-accent-300" />
                </div>
                <IconChip icon={CheckVerified02} color="accent" />
            </div>
            <p className="text-sm text-tertiary">
                برداشت به کیف پول میزبان‌فا <span className="font-medium text-success-primary">۵٪ هدیه</span> دارد؛ برداشت به حساب بانکی هم ممکن است.
            </p>
            <div className="mt-auto flex flex-col gap-2">
                {gate.disabled ? (
                    <Button size="lg" iconLeading={ArrowCircleBrokenDown} isDisabled className="w-full">
                        برداشت
                    </Button>
                ) : (
                    <Button href="/wallet" size="lg" iconLeading={ArrowCircleBrokenDown} className="w-full">
                        برداشت
                    </Button>
                )}
                {gate.reason && <p className="text-center text-xs leading-5 text-tertiary">{gate.reason}</p>}
            </div>
        </section>

        {/* Pending */}
        <section className={cardClass} aria-labelledby="pending-label">
            <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1">
                    <p id="pending-label" className="flex items-center gap-1.5 text-sm font-medium text-tertiary">
                        در انتظار
                        <HelpTip term="pending" />
                    </p>
                    <Money rial={balances.pendingRial} size="display-sm" className="font-semibold text-primary" />
                </div>
                <IconChip icon={Hourglass03} color="brand" />
            </div>
            <p className="text-sm text-tertiary">{balances.pendingCount} پورسانت در دورهٔ ضمانت ۱۴ روزه است.</p>
            {balances.nextRelease && (
                <div className="mt-auto flex flex-col gap-1.5 rounded-xl bg-brand-primary_alt p-3.5 ring-1 ring-brand-200 ring-inset dark:bg-brand-500/10 dark:ring-brand-500/25">
                    <p className="text-xs font-medium text-tertiary">مبلغ بعدی که آزاد می‌شود</p>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <Money rial={balances.nextRelease.amountRial} size="md" className="font-semibold text-primary" />
                        <Countdown target={balances.nextRelease.at} className="font-medium text-brand-secondary dark:text-brand-300" />
                    </div>
                </div>
            )}
        </section>

        {/* Paid to date: the big trust number */}
        <section
            className="relative isolate flex flex-col justify-between gap-6 overflow-hidden rounded-2xl bg-brand-gradient p-5 text-white shadow-lg ring-1 ring-white/10 ring-inset md:col-span-2 md:p-6 xl:col-span-1"
            aria-labelledby="paid-label"
        >
            <BackgroundPattern pattern="grid" size="lg" className="absolute -top-24 -end-24 -z-10 text-white/10" aria-hidden="true" />
            <div className="absolute -bottom-16 -start-16 -z-10 size-56 rounded-full bg-accent-500/25 blur-3xl" aria-hidden="true" />
            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between gap-3">
                    <p id="paid-label" className="text-md font-medium text-secondary_on-brand">
                        پرداخت‌شده تا امروز
                    </p>
                    <span aria-hidden="true" className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white/12 text-accent-300 ring-1 ring-white/15 ring-inset">
                        <BankNote03 className="size-5" />
                    </span>
                </div>
                <Money rial={balances.paidToDateRial} size="display-md" className="font-bold text-white" unitClassName="text-secondary_on-brand" />
                {balances.firstPayoutAt ? (
                    <p className="text-sm text-tertiary_on-brand">
                        در {balances.payoutCount} تسویه، از <JalaliDate value={balances.firstPayoutAt} format="monthYear" /> تا امروز
                    </p>
                ) : (
                    <p className="text-sm text-tertiary_on-brand">هنوز تسویه‌ای انجام نشده است.</p>
                )}
            </div>
            {lifetime && lifetime.totalRial !== "0" && (
                <div className="flex flex-col gap-0.5 border-t border-white/15 pt-3">
                    <span className="flex flex-wrap items-baseline justify-between gap-2 text-sm text-tertiary_on-brand">
                        {lifetime.legacyRial !== "0" ? "پورسانت ثبت‌شده از ابتدا" : "پورسانت کل از ابتدا"}
                        <Money rial={lifetime.totalRial} size="md" className="font-semibold text-white" unitClassName="text-tertiary_on-brand" />
                    </span>
                    {lifetime.legacyRial !== "0" && (
                        <span className="text-xs text-tertiary_on-brand">
                            شامل <Money rial={lifetime.legacyRial} size="xs" unit={false} className="text-secondary_on-brand" /> تومان پورسانت ثبت‌شده در سیستم قبلی؛ بعضی
                            از پورسانت‌های قدیمی همان‌جا لغو یا اصلاح شده‌اند، برای همین این عدد با جمع پرداخت‌شده و موجودی برابر نیست.
                        </span>
                    )}
                </div>
            )}
            {balances.lastPayout && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-white/10 px-4 py-3 ring-1 ring-white/15 backdrop-blur-sm">
                    <div className="flex flex-col">
                        <span className="text-xs text-tertiary_on-brand">آخرین واریز</span>
                        <JalaliDate value={balances.lastPayout.at} className="text-sm font-medium text-white" />
                    </div>
                    <div className="flex flex-col items-end">
                        <span className="text-xs text-tertiary_on-brand">{balances.lastPayout.channel === "wallet" ? "به کیف پول میزبان‌فا" : "به حساب بانکی"}</span>
                        <Money rial={balances.lastPayout.amountRial} size="sm" className="font-semibold text-white" unitClassName="text-tertiary_on-brand" />
                    </div>
                </div>
            )}
        </section>
    </div>
    );
};
